import React from 'react';
import PropTypes from 'prop-types';
import { ProgressBar } from 'react-bootstrap';
import classnames from 'classnames';
import dayjs from '../util/date.js';
import {
	calendarForActivity,
	defaultCalendarId,
	resolveCalendar,
	progressPointMs,
	segmentPercentNow,
	showGanttNonWorkingTime,
	splitIntoNonWorkSegments,
	splitIntoWorkSegments
} from '../util/workCalendar.js';

const ActivitySummary = ({
	activity,
	datasetInfo
}) => {
	const formatDate = date => {
		return dayjs(parseInt(date)).format('DD-MMM-YYYY');
	};

	// This needs to be here because render() is called here AFTER the datasetInfo has changed but BEFORE the activities have changed.
	// I think it'll be fixed when render() is only called when it's necessary. Right now it's called many times, even just opening/closing the menu.
	// projects may be undefined/[] when getDatasets never populated PROJECT rows — never use `in` on a non-object.
	const projects = datasetInfo.projects && !Array.isArray(datasetInfo.projects)
		? datasetInfo.projects
		: null;
	// Only bail for the stale-activities race (map loaded, but this projId isn't in it).
	if (projects && Object.keys(projects).length > 0 && !(activity.projId in projects)) return null;
	const projectMeta = (projects && projects[activity.projId]) || {};

	const remainingDurationUnit = datasetInfo.RemainingDurationUnit === 'days' ? 'd' : 'hr';
	const hrsPerDay = datasetInfo.HoursPerDay || 1;
	const startDisplay = formatDate(activity.start) + (activity.start === activity.actualStart ? ' A' : '');
	const finishDisplay = formatDate(activity.finish) + (activity.finish === activity.actualFinish ? ' A' : '');
	const dateDisplay = activity.taskType === 'TT_Mile'
		? startDisplay :
		activity.taskType === 'TT_FinMile' ? finishDisplay : startDisplay + ' - ' + finishDisplay;
	const tfDisplay = activity.statusCode === 'TK_Complete' ? '' : ` | TF: ${(activity.totalFloat / hrsPerDay).toFixed(1)} ${remainingDurationUnit}`;

	// Gantt scale is always the project setup timescale (not the date filter window).
	const scaleStart = Number(datasetInfo.StartDate) || 0;
	const scaleEnd = Number(datasetInfo.EndDate) || 0;
	const projectRange = scaleStart && scaleEnd && scaleEnd > scaleStart ? scaleEnd - scaleStart : 0;

	const placeOnScale = (startMs, finishMs) => {
		if (!projectRange || !startMs || !finishMs) {
			return null;
		}
		const range = finishMs - startMs || 86400000;
		return {
			left: ((startMs - scaleStart) / projectRange) * 100,
			width: range / projectRange * 100
		};
	};

	let labels = [];
	let left;
	let width;
	if (projectRange) {
		const placed = placeOnScale(activity.start, activity.finish);
		if (placed) {
			width = placed.width;
			left = placed.left;
			if (left < 0) {
				const beginsOrEnds = left + width < 0 ? 'ends' : 'begins';
				labels.push(<span key='start'>This activity {beginsOrEnds} before the project start date ({dayjs(scaleStart).format('DD-MMM-YYYY')}).</span>);
			}
			if (left + width > 100) {
				const beginsOrEnds = left > 100 ? 'begins' : 'ends';
				labels.push(<span key='end'>This activity {beginsOrEnds} after the project end date ({dayjs(scaleEnd).format('DD-MMM-YYYY')}).</span>);
			}
		}
	}

	if (labels.length) {
		labels = (<div>{labels}</div>);
	}

	const isComplete = activity.statusCode === 'TK_Complete';
	const criticalThreshold = Number(projectMeta.criticalDrtnHrCnt) || 0;
	const isCritical = activity.drivingPathFlag === 'Y' || activity.totalFloat < criticalThreshold;
	const colors = {
		darkBlue: '#337ab7',
		red: '#d9534f',
		green: '#5cb85c'
	};

	const shadeCal = resolveCalendar(
		null,
		datasetInfo.calendars,
		projectMeta.clndrId != null ? projectMeta.clndrId : projectMeta.clndr_id,
		defaultCalendarId(datasetInfo.calendars)
	);
	const activityCal = calendarForActivity(activity, datasetInfo);
	const showNonWorking = showGanttNonWorkingTime(datasetInfo);
	const workSegments = (!showNonWorking || activity.taskType === 'TT_Mile' || activity.taskType === 'TT_FinMile')
		? []
		: splitIntoWorkSegments(activity.start, activity.finish, activityCal);
	const nonWorkShade = showNonWorking && projectRange && activity.start && activity.finish
		? splitIntoNonWorkSegments(activity.start, activity.finish, shadeCal)
		: [];
	const progressAt = showNonWorking
		? progressPointMs(activity.start, activity.finish, activity.percentComplete, activityCal)
		: null;
	const baselineLookup = datasetInfo.baselineByTask && (
		datasetInfo.baselineByTask[activity.activityId]
		|| datasetInfo.baselineByTask[String(activity.activityId)]
	);
	const baselineStart = Number(activity.baselineStart || (baselineLookup && baselineLookup.start)) || 0;
	const baselineFinish = Number(activity.baselineFinish || (baselineLookup && baselineLookup.finish)) || 0;
	const baselineTaskType = activity.baselineTaskType
		|| (baselineLookup && baselineLookup.taskType)
		|| activity.taskType;
	const baselinePlaced = placeOnScale(baselineStart, baselineFinish);
	const baselineIsMile = baselineTaskType === 'TT_Mile' || baselineTaskType === 'TT_FinMile';
	const baselineStyles = {};
	if (baselinePlaced) {
		if (baselinePlaced.left < 100) {
			baselineStyles.left = `${baselinePlaced.left}%`;
		} else {
			baselineStyles.right = '0';
		}
		if (!baselineIsMile) {
			baselineStyles.width = `${baselinePlaced.width}%`;
		}
	}
	const baselineGraphic = baselinePlaced
		? (
			baselineIsMile
				? <div className="milestoneDiamondBaseline" style={baselineStyles} />
				: <div className="progressBarBaseline" style={baselineStyles} />
		)
		: null;

	const statusNoun = isComplete
		? 'Complete'
		: (activity.statusCode === 'TK_Active' ? 'Active' : 'Not started');
	const criticalNoun = isCritical ? 'critical' : 'not critical';
	const progressLabel = `${statusNoun}, ${criticalNoun}, ${activity.percentComplete}% complete`;
	let progress = null;
	const styles = {};
	if (typeof left === 'number') {
		if (left < 100) {
			styles.left = `${left}%`;
		} else {
			styles.right = '0';
		}

		let colorClass = '';
		if (activity.taskType === 'TT_Mile' || activity.taskType === 'TT_FinMile') {
			if (isComplete) {
				styles.backgroundColor = colors.darkBlue;
				colorClass = ' important-color-darkBlue';
			} else if (isCritical) {
				styles.backgroundColor = colors.red;
				colorClass = ' important-color-red';
			} else {
				styles.backgroundColor = colors.green;
				colorClass = ' important-color-green';
			}

			progress = (
				<div
					className={classnames('milestoneDiamondWrapper', { 'milestoneDiamondWrapper--withBaseline': !!baselineGraphic })}
					role="img"
					aria-label={progressLabel}>
					<div className={`milestoneDiamond${colorClass}`} style={styles}>
					</div>
					{baselineGraphic}
				</div>
			);
		} else {
			// TT_LOE / TT_WBS stay thinner than normal task bars (height via CSS modifier).
			const isSummaryBar = activity.taskType === 'TT_LOE' || activity.taskType === 'TT_WBS';
			if (isCritical) {
				colorClass = ' important-color-red';
			} else {
				colorClass = ' important-color-green';
			}
			const segments = workSegments.length
				? workSegments
				: [{ start: activity.start, finish: activity.finish }];
			const shade = nonWorkShade.map((seg, i) => {
				const placed = placeOnScale(seg.start, Math.max(seg.start + 1, seg.finish - 1));
				if (!placed) {
					return null;
				}
				return (
					<div
						key={`nw-${i}`}
						className="progressBarNonWork"
						style={{ left: `${placed.left}%`, width: `${placed.width}%` }} />
				);
			});
			const bars = segments.map((seg, i) => {
				const placed = placeOnScale(seg.start, seg.finish);
				if (!placed) {
					return null;
				}
				const segStyle = {
					left: `${placed.left}%`,
					width: `${placed.width}%`,
					backgroundColor: isCritical ? colors.red : colors.green
				};
				return (
					<ProgressBar
						key={`seg-${i}`}
						className={classnames(colorClass, { 'progress--summary': isSummaryBar })}
						now={progressAt == null
							? activity.percentComplete
							: segmentPercentNow(seg.start, seg.finish, progressAt)}
						style={segStyle}
						bsStyle="info"
						aria-label={i === 0 ? progressLabel : undefined} />
				);
			});
			progress = (
				<div className={classnames('progressBarWrapper', {
					'progressBarWrapper--summary': isSummaryBar,
					'progressBarWrapper--withBaseline': !!baselineGraphic
				})}>
					{shade}
					{bars}
					{baselineGraphic}
				</div>
			);
		}
	} else if (baselineGraphic) {
		progress = baselineIsMile
			? (
				<div className="milestoneDiamondWrapper milestoneDiamondWrapper--withBaseline milestoneDiamondWrapper--baselineOnly">
					{baselineGraphic}
				</div>
			)
			: (
				<div className="progressBarWrapper progressBarWrapper--withBaseline progressBarWrapper--baselineOnly">
					{baselineGraphic}
				</div>
			);
	}

	const mainClasses = classnames('activitySummary', {hasChanged: activity.hasChanged}),
		flagClasses = classnames('flag', 'glyphicon', 'glyphicon-flag', {visible: activity.flag, [`flag-${activity.flag}`]: activity.flag});
	return (
		<div className={mainClasses}>
			<div className="activityHeader">{activity.name}<span className={flagClasses} aria-label={activity.flag ? `Flagged ${activity.flag}` : undefined}></span></div>
			<div className="activitySummaryInfo">
				<span>{activity.taskCode} | {activity.percentComplete} % | </span>
				{dateDisplay}
				{tfDisplay}
				{labels}
			</div>
			{progress}
		</div>
	);
};

ActivitySummary.propTypes = {
	activity: PropTypes.object.isRequired,
	datasetInfo: PropTypes.object.isRequired
};

export default ActivitySummary;